import axios, { AxiosHeaders, InternalAxiosRequestConfig } from "axios";
import { useEffect } from "react";
import { api } from "../settings";
import { useAuth } from "../src/auth/useAuth";
import { clearTokens, deriveExpiresAt, getValidAccessToken, persistTokens } from "../services/token-manager";

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const ensureAxiosHeaders = (headers?: InternalAxiosRequestConfig["headers"]) =>
  headers instanceof AxiosHeaders ? headers : new AxiosHeaders(headers ?? {});

// Create instance outside the hook so it's stable across all components
const instance = axios.create({
  baseURL: api.endpoint,
  withCredentials: true,
});

// Set up interceptors once at module level - these run for every request
instance.interceptors.request.use(async (config: RetriableConfig) => {
  const headers = ensureAxiosHeaders(config.headers);

  if (!headers.has("Authorization")) {
    try {
      const token = await getValidAccessToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    } catch {}
  }

  return { ...config, headers };
});

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status !== 401) {
      return Promise.reject(error);
    }

    const originalRequest: RetriableConfig | undefined = error.config;

    if (!originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const token = await getValidAccessToken();
      if (token) {
        const headers = ensureAxiosHeaders(originalRequest.headers);
        headers.set("Authorization", `Bearer ${token}`);
        return instance({ ...originalRequest, headers });
      }

      window.location.href = "/";
      return Promise.reject(new Error("Session expired"));
    } catch (refreshError) {
      window.location.href = "/";
      return Promise.reject(refreshError);
    }
  },
);

const useApi = () => {
  const { isAuthenticated, accessToken } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      clearTokens();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (accessToken) {
      const expiresAt = deriveExpiresAt(accessToken);
      persistTokens({
        accessToken,
        ...(typeof expiresAt === "number" ? { expiresAt } : {}),
      });
    }
  }, [accessToken]);

  return { instance };
};

export default useApi;
